import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import _ from 'lodash';
import {Amount, CustomerVopChoice, FeesTypeEnum, VopBeneficiaryStatus} from '../../../@types/PaymentProTypes';
import {AccountType, Beneficiary, BeneficiaryAddress} from '../../../@types/BeneficiaryTypes';
import {Concat} from '../../../../@types/utils';
import {formatISO} from 'date-fns';

export enum SingleTransferToType {
  MY_ACCOUNTS = 'MY_ACCOUNTS',
  BENEFICIARY = 'BENEFICIARY'
}

export enum PaymentTypeType {
  RTGS = 'RTGS',
  SALARY = 'SALARY',
  URGENT = 'URGENT',
  CLASSIC = 'CLASSIC',
  INST = 'INST'
}

export type PaymentTypeRequest = {
  currency: string,
  debitedAccount: string,
  creditedAccount: string,
  creditedCountryCode: string
};

export type FeesRequest = {
  amount: number,
  currency: string,
  countryOfDestination: string
};

export type SinglePaymentState = {
  paymentData: CreateAdvancedTransferData;
  step: number;
};

export enum TransferToType {
  BGL_BGL = 'BGL_BGL',
  BENEFICIARY_SAVED = 'BENEFICIARY_SAVED',
  BENEFICIARY_OTHER = 'BENEFICIARY_OTHER'
}

export type PrivateTransferBeneficiary = {
  accountReference: string | null;
  contractReference: string | null;
  beneficiaryAccountType: AccountType;
};

export type CreateAdvancedTransferData = {
  contractReference: string | null,
  primaryContract: string | null,

  accountFrom: string,
  beneficiary: PrivateTransferBeneficiary | Beneficiary,

  communication: string | null,
  amount: Amount,
  accountCurrency?: string | null,
  endToEndReference: string,
  executionDate: string | null,
  transferRequestFees: FeesTypeEnum | null,
  regulatoryReportingReason: string | null,

  salary: boolean,
  rtgs: boolean,
  urgent: boolean,
  classic: boolean,
  inst: boolean,

  debitAdvice: boolean,
  saveBeneficiary: boolean,
  transferToType?: TransferToType,

  voPResult?: {
    uuid: string | null,
    status: VopBeneficiaryStatus | null,
    vopBeneficiaryName: string | null,
    choice: CustomerVopChoice | null,
    vopNameExtended?: string | null
  }

  signatureRequest?: {
    challengeId: string,
    answer: string
  }
};

export type TransferResultResponse = {
  referenceOperation: string;
  preInstruction: boolean;
  transferWarnings: string[];
};

const initialState: SinglePaymentState = {
  paymentData: {
    contractReference: null,
    primaryContract: null,

    accountFrom: '',
    beneficiary: {
      contractReference: '',
      accountReference: '',
      beneficiaryAccountType: AccountType.IBAN
    },

    communication: null,
    amount: {
      value: null,
      currency: 'EUR'
    },
    accountCurrency: null,
    endToEndReference: '',
    executionDate: formatISO(new Date(), {representation: 'date'}),
    transferRequestFees: FeesTypeEnum.REGULAR,
    regulatoryReportingReason: '',

    salary: false,
    rtgs: false,
    urgent: false,
    classic: true,
    inst: false,

    debitAdvice: true,
    saveBeneficiary: false,
    transferToType: TransferToType.BGL_BGL
  },
  step: 1
};


export type CreateAdvancedTransferDataFieldType =
  Omit<keyof CreateAdvancedTransferData, 'amount' | 'beneficiary'>
  | Concat<'amount.', keyof Amount>
  | Concat<'beneficiary.', keyof Beneficiary>;

const nullifEmpty = (val: string) => {
  return (val === null || /^\s*$/.test(String(val))) ? null : val;
};

const singlePaymentSlice = createSlice({
  initialState,
  name: 'payment/singlePayment',
  reducers: {
    setPaymentData(state, action: PayloadAction<{ field: CreateAdvancedTransferDataFieldType, value: any }>) {
      if(action.payload.field === 'beneficiary.foreignNationalReference'){
        //Set to null if empty or only spaces to pass @Valid
        _.set(state.paymentData, action.payload.field as string, nullifEmpty(action.payload.value));
      } else {
        _.set(state.paymentData, action.payload.field as string, action.payload.value);
      }
    },
    setPaymentBeneficiaryData(state, action: PayloadAction<{ value: Beneficiary }>) {
      _.set(state.paymentData, 'beneficiary', action.payload.value);
    },
    setPaymentBeneficiaryFieldData(state, action: PayloadAction<{ field: keyof Beneficiary, value: string }>) {
      if(action.payload.field === 'foreignNationalReference'){
        _.set(state.paymentData, action.payload.field as string, nullifEmpty(action.payload.value));
      } else {
        _.set(state.paymentData, `beneficiary.${action.payload.field}`, action.payload.value);
      }
    },
    setPaymentBeneficiaryAddressFieldData(state, action: PayloadAction<{
      field: keyof BeneficiaryAddress;
      value: any
    }>) {
      _.set(state.paymentData, 'beneficiary.beneficiaryAddresses[0].' + action.payload.field, action.payload.value);
    },
    setStep(state, action: PayloadAction<number>) {
      state.step = action.payload;
    },
    setTransferToType(state, action: PayloadAction<TransferToType>) {
      _.set(state.paymentData, 'transferToType', action.payload);
    },
    resetBeneficiary(state) {
      _.set(state.paymentData, 'beneficiary', initialState.paymentData.beneficiary);
    },
    resetPaymentData(state) {
      state.paymentData = initialState.paymentData;
    },
    resetVopData(state) {
      _.set(state.paymentData, 'voPResult', null);
    }
  }
});

export const {
  setPaymentData,
  setPaymentBeneficiaryData,
  setPaymentBeneficiaryFieldData,
  setPaymentBeneficiaryAddressFieldData,
  setStep,
  setTransferToType,
  resetBeneficiary,
  resetPaymentData,
  resetVopData
} = singlePaymentSlice.actions;

export const reducer = singlePaymentSlice.reducer;
